import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import React from 'react';
import { useSelector } from 'react-redux';
import { AulaState } from '@core/types/state';
import { getCurrentSpaceId } from '@core/selectors/space';
import NavigationBackHeader from '@app/components/presentational/common/NavigationBackHeader/NavigationBackHeader';
import pushRoute from '@app/utils/routing';
import { getAssignmentsBaseRoute } from '../utils/navigation';
import { createStyles } from './SubmissionsResponsive.styles';

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

/**
 * Fallback: derive the submission segment from Redux routing state.
 * Used only when the router hasn't injected submissionId via params
 * (e.g. on the base `.../submissions` route).
 */
const selectCurrentSubmissionId = (state: AulaState): string | undefined => {
  const { routing } = state as any;
  if (!routing) return undefined;
  const { pathname } = routing.locationBeforeTransitions;
  const idx = pathname.search('/submissions');
  if (idx === -1) return undefined;
  const [segment] = pathname
    .substring(idx)
    .replace('/submissions', '')
    .replace(/^\//, '')
    .split('/');
  return segment || undefined;
};

// ---------------------------------------------------------------------------
// Dummy sidebar — replace with real SubmissionSidebar when ready
// ---------------------------------------------------------------------------
const DUMMY_SUBMISSIONS = [
  { id: 'submission-1', label: 'Submission 1' },
  { id: 'submission-2', label: 'Submission 2' },
  { id: 'submission-3', label: 'Submission 3' },
];

type SidebarProps = {
  backRoute: string;
  onSubmissionClick: (id: string) => void;
};

const DummySubmissionSidebar = ({
  backRoute,
  onSubmissionClick,
}: SidebarProps) => (
  <Box
    sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}
  >
    {/* Matches AssignmentSidebar buttonWrapper exactly: px 16px mobile / 12px desktop,
        pt 16px mobile / 12px desktop, marginLeft -8px — keeps back button in the
        same visual position on every page so navigation never jumps. */}
    <Box
      sx={{
        px: { xs: '16px', md: '12px' },
        pt: { xs: '16px', md: '12px' },
        marginLeft: '-8px',
      }}
    >
      <NavigationBackHeader route={backRoute} />
    </Box>
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        px: '12px',
        minHeight: 0,
      }}
    >
      <Typography variant="h6" component="h2" sx={{ my: '16px' }}>
        Submissions
      </Typography>
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {DUMMY_SUBMISSIONS.map(({ id, label }) => (
          <Box
            key={id}
            onClick={() => onSubmissionClick(id)}
            sx={{
              p: '12px 16px',
              borderRadius: '8px',
              cursor: 'pointer',
              '&:hover': { backgroundColor: 'action.hover' },
            }}
          >
            <Typography variant="body1">{label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);

// ---------------------------------------------------------------------------
// Dummy content — replace with real SubmissionDetails when ready
// ---------------------------------------------------------------------------
const DummySubmissionContent = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '120px',
    }}
  >
    <Typography variant="body1" color="text.secondary">
      Select a submission to view details.
    </Typography>
  </Box>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
type Props = {
  params?: {
    assignmentId?: string;
    submissionId?: string;
  };
};

const SubmissionsResponsive = ({ params }: Props) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  const spaceId = useSelector((state: AulaState) => getCurrentSpaceId(state));
  // Prefer the submissionId injected by the router (available immediately on
  // hard refresh) and fall back to parsing the Redux routing state (used when
  // landing on the base `.../submissions` route that has no :submissionId param).
  const reduxSubmissionId = useSelector(selectCurrentSubmissionId);
  const currentSubmissionId = params?.submissionId ?? reduxSubmissionId;

  const assignmentRoute = params?.assignmentId
    ? `${getAssignmentsBaseRoute(spaceId)}/${params.assignmentId}`
    : `${getAssignmentsBaseRoute(spaceId)}/list`;

  const submissionsBaseRoute = `${getAssignmentsBaseRoute(spaceId)}/${
    params?.assignmentId
  }/submissions`;

  const goToSubmission = (submissionId: string) =>
    pushRoute(`${submissionsBaseRoute}/${submissionId}`);

  // Treat "no trailing segment" the same as "list" — this is the submissions
  // list view. A refresh on `.../submissions` should keep the list visible, not
  // redirect anywhere.
  const isListView = !currentSubmissionId || currentSubmissionId === 'list';

  // ── Desktop: sidebar + content always side-by-side ─────────────────────────
  if (isMdUp) {
    return (
      <Box sx={styles.desktopShell}>
        <Box sx={styles.leftWrapper}>
          <DummySubmissionSidebar
            backRoute={assignmentRoute}
            onSubmissionClick={goToSubmission}
          />
        </Box>
        <Box sx={styles.contentArea}>
          <DummySubmissionContent />
        </Box>
      </Box>
    );
  }

  // ── Mobile list view ────────────────────────────────────────────────────────
  if (isListView) {
    return (
      <Box sx={{ height: '100%' }}>
        <DummySubmissionSidebar
          backRoute={assignmentRoute}
          onSubmissionClick={goToSubmission}
        />
      </Box>
    );
  }

  // ── Mobile detail view ──────────────────────────────────────────────────────
  // Back button goes to the submissions list (`.../submissions/list`).
  // White container wraps the detail content, matching the Assignment detail
  // mobile layout (AssignmentResponsive → pageContent).
  const submissionsListRoute = `${submissionsBaseRoute}/list`;

  return (
    <Box sx={styles.mobilePage}>
      <Box sx={styles.backButtonWrapper}>
        <NavigationBackHeader route={submissionsListRoute} />
      </Box>
      <Box sx={styles.mobileContentWrapper}>
        <DummySubmissionContent />
      </Box>
    </Box>
  );
};

export default SubmissionsResponsive;
